import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import {
  filterValuesSeparator,
  filterValuesSeparatorSplitRegex
} from 'shared-helpers/consts';
import { DropdownSelectable } from 'components/common/dropdown';
import { StatCount } from 'components/common/stats';

type UsersView =
  | 'overview'
  | 'completed'
  | 'attributes'
  | `course${string}${string}` // course${filterValuesSeparator}${courseTitle}
  | `path${string}${string}` // path${filterValuesSeparator}${pathTitle}
  | `achievement${string}${string}`; // achievement${filterValuesSeparator}${achievementTitle}

type ViewState = { subText?: string; text: string; value: UsersView }[];

const UsersViewSelector = () => {
  const {
    messages,
    pageState: {
      loading,
      usersAchievementData,
      usersCourseData,
      usersPathData
    },
    serverConfig: { userCustomAttributes, platformCompetenciesOnly }
  } = useReduxState();

  const isNavigating = useRef(false);

  const router = useRouter();

  const [availableViews, setAvailableView] = useState<ViewState>([
    { text: messages.commonOverview, value: 'overview' },
    { text: messages.commonCompleted, value: 'completed' }
  ]);

  useEffect(() => {
    const handleRouteChangeStart = () => (isNavigating.current = true);
    const handleRouteChangeComplete = () => (isNavigating.current = false);

    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChangeComplete);

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, [router.events]);

  useEffect(() => {
    if (loading) return;

    setAvailableView([
      { text: messages.commonOverview, value: 'overview' },
      ...(userCustomAttributes?.length
        ? [
            {
              text: messages.commonAttributes,
              value: 'attributes' as UsersView
            }
          ]
        : []),
      ...(!platformCompetenciesOnly
        ? [{ text: messages.commonCompleted, value: 'completed' as UsersView }]
        : []),
      ...Object.keys(usersCourseData || {}).map((course) => ({
        subText: messages.courseAnalytics,
        text: course,
        value: `course${filterValuesSeparator}${course}` as UsersView
      })),
      ...Object.keys(usersPathData || {}).map((path) => ({
        subText: messages.pathAnalytics,
        text: path,
        value: `path${filterValuesSeparator}${path}` as UsersView
      })),
      ...Object.keys(usersAchievementData || {}).map((achievement) => ({
        subText: messages.achievementAnalytics,
        text: achievement,
        value: `achievement${filterValuesSeparator}${achievement}` as UsersView
      }))
    ]);
  }, [
    loading,
    userCustomAttributes,
    usersCourseData,
    usersPathData,
    usersAchievementData
  ]);

  useEffect(() => {
    const { view } = router.query || {};
    if (loading || !view?.includes(filterValuesSeparator)) return;

    const product = (view as string).split(filterValuesSeparatorSplitRegex)[0];
    const title = (view as string)
      .split(filterValuesSeparatorSplitRegex)
      .slice(1)
      .join(filterValuesSeparator);
    if (
      (product === 'course' && usersCourseData?.[title]) ||
      (product === 'path' && usersPathData?.[title]) ||
      (product === 'achievement' && usersAchievementData?.[title])
    ) {
      return;
    } else if (!isNavigating.current) handleChange('overview');
  }, [loading, router.query]);

  const handleChange = (view: UsersView) => {
    const query = { ...router.query, view };
    router.replace({ query }, undefined, { shallow: true });
  };

  const numberOfNonDefaultViews =
    Object.keys(usersAchievementData || {}).length +
    Object.keys(usersCourseData || {}).length +
    Object.keys(usersPathData || {}).length;

  return (
    <div className="view" style={{ position: 'relative' }}>
      {numberOfNonDefaultViews > 0 ? (
        <div style={{ position: 'absolute', right: -10, top: -12, zIndex: 2 }}>
          <StatCount category="darker" value={`+${numberOfNonDefaultViews}`} />
        </div>
      ) : null}

      <DropdownSelectable<UsersView>
        category="lighter"
        defaultText={`${messages.commonSelect}...`}
        defaultValue={(router.query?.view || 'overview') as UsersView}
        dropdownTextMinWidth="120px"
        onChange={handleChange}
        options={availableViews}
        optionStyle={{ maxWidth: 300 }}
        selectedValueBefore={`${messages.commonView}:`}
        showSelectedText={true}
        size="header"
        titleMaxWidth={120}
        value={(router.query?.view || 'overview') as UsersView}
      />
    </div>
  );
};

export default UsersViewSelector;
